  import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface EmploiItem {
  id: number;
  idStudent: number;
  batchId: number;
  dayOfTheWeek: string;
  timeSlot: string;
  subject: string;
  teacher: string;
  classroom: string;
  startTime: number;
  endTime: number;
  color?: string;
  batch?: { batchname: string };
}

export interface EmploiResponse {
  data: EmploiItem[];
  total: number;
  batchId: number;
  dayOfTheWeek: string;
}

@Injectable({
  providedIn: 'root'
})
export class EmploiService {
  private baseUrl = `${environment.apiUrl.replace(/\/$/, '')}/emploi`;

  constructor(private http: HttpClient) { }

  /** Récupérer l'emploi du temps d'un élève pour un jour donné */
  getEmploiByIdBatch(batchId: number, dayOfWeek: string, idStudent: number): Observable<EmploiResponse> {
    const url = `${this.baseUrl}/getEmploiStudent/${batchId}/${dayOfWeek}/${idStudent}`;

    return this.http.get<any>(url).pipe(
      map(res => {
        const tableUnit = res.response?.results?.tableUnit || [];
        const data: EmploiItem[] = tableUnit.map((item: any) => ({
          id: item.id,
          idStudent: idStudent,
          batchId: batchId,
          dayOfTheWeek: item.dayOfTheWeek,
          timeSlot: `${item.seance.start}-${item.seance.end}`,
          subject: item.courseUnitBatchTeacher?.courseUnitDef?.name || '',
          teacher: item.courseUnitBatchTeacher?.teacher?.user?.firstName || '',
          classroom: item.classRoom?.alphaDigitRef || '',
          startTime: item.seance.start,
          endTime: item.seance.end,
          color: item.color || this.getSubjectColor(item.courseUnitBatchTeacher?.courseUnitDef?.name),
          batch: item.batch ? { batchname: item.batch.batchname } : undefined
        }));

        return {
          data,
          total: res.response?.totalResults ?? 0,
          batchId,
          dayOfTheWeek: dayOfWeek
        } as EmploiResponse;
      })
    );
  }

  /** Récupérer l'emploi de la semaine complète */
  getWeeklySchedule(batchId: number, idStudent: number): Observable<EmploiItem[][]> {
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const requests = daysOfWeek.map(day =>
      this.getEmploiByIdBatch(batchId, day, idStudent).pipe(map(res => res.data))
    );
    return forkJoin(requests);
  }

  /** Transformer l'emploi en format {jour: {horaire: item}} */
  transformToScheduleFormat(
    emploiData: EmploiItem[][],
    daysOfWeek: string[] = ['Mon','Tue','Wed','Thu','Fri','Sat']
  ): { [key: string]: { [key: string]: any } } {

    const schedule: { [key: string]: any } = {};

    const toHHMM = (minutes: number) => {
      const h = Math.floor(minutes / 60).toString().padStart(2, '0');
      const m = (minutes % 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    };

    emploiData.forEach((dayArray, index) => {
      const day = daysOfWeek[index];
      if (!dayArray || !day) return;

      dayArray.forEach(item => {
        const timeSlot = `${toHHMM(item.startTime)}-${toHHMM(item.endTime)}`;
        if (!schedule[day]) schedule[day] = {};
        schedule[day][timeSlot] = {
          subject: item.subject,
          teacher: item.teacher,
          classroom: item.classroom,
          color: item.color
        };
      });
    });

    return schedule;
  }

  /** Couleur par matière */
  private getSubjectColor(subject: string): string {
    const colors: { [key: string]: string } = {
      'رياضيات': '#FFE4B5',
      'علوم': '#E6F3FF',
      'عربي': '#E8F5E8',
      'فيزياء': '#FFE4E1',
      'كيمياء': '#F0E68C',
      'أحياء': '#E8F5E8',
      'تاريخ': '#E6E6FA',
      'جغرافيا': '#F5DEB3',
      'لغة إنجليزية': '#E0FFFF',
      'تربية إسلامية': '#F0FFF0',
      'فنون': '#FFF0F5'
    };
    return colors[subject] || '#F8F9FA';
  }
}
